import { oc } from "@orpc/contract";
import { z } from "zod";
import { OptionalOffsetPageShape } from "../../schemas/common";

/**
 * Read-time "copy / transfer sales history" links (`im_sales_history_transfers`).
 *
 * A row says "demand recorded at scope X should be read as if it happened at
 * scope Y", on the product axis, the warehouse axis, or both. `copy` leaves the
 * source's own history intact; `transfer` also removes the moved share from it.
 * See the model doc-comment for the full semantics.
 *
 * Lifecycle is soft-revoke only, so the list surface returns revoked rows too on
 * request — the UI shows a rule's history, not just its current state.
 */

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const SalesHistoryTransferModeSchema = z.enum(["copy", "transfer"]);
export type SalesHistoryTransferModeInput = z.infer<
  typeof SalesHistoryTransferModeSchema
>;

/** One rule row, active or revoked. */
export const SalesHistoryTransferRowSchema = z.object({
  id: z.string().uuid(),
  mode: SalesHistoryTransferModeSchema,
  /** (0, 100]. DECIMAL in the DB; the API speaks numbers. */
  copyPercent: z.number(),
  /** Both set (a SKU remap) or both null (a warehouse-move rule). */
  sourceProductV3Key: z.string().nullable(),
  targetProductV3Key: z.string().nullable(),
  /** The SKU a buyer reads; null with the key, or when the catalog has none. */
  sourceProductSku: z.string().nullable(),
  targetProductSku: z.string().nullable(),
  /** Both set or both null. */
  sourceWarehouseId: z.string().uuid().nullable(),
  targetWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  targetWarehouseName: z.string().nullable(),
  customerOriginCode: z.string().nullable(),
  /** YYYY-MM-DD; only demand months strictly before it are remapped. */
  effectiveBefore: z.string().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  /** Null for ingested rules, which no person authored. */
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  deactivatedAt: z.string().nullable(),
  isActive: z.boolean(),
  /** Provenance badge: null/'manual' for hand-authored, else the ingest source. */
  originSystem: z.string().nullable(),
  originEntity: z.string().nullable(),
});
export type SalesHistoryTransferRow = z.infer<
  typeof SalesHistoryTransferRowSchema
>;

// ─── List ────────────────────────────────────────────────────────────────────

export const ListSalesHistoryTransfersInputSchema = z.object({
  /** Matches rows naming this SKU on EITHER side. Omit for org-wide. */
  productV3Key: z.string().min(1).optional(),
  /** Matches rows naming this warehouse on EITHER side. Omit for org-wide. */
  warehouseId: z.string().uuid().optional(),
  /** Include soft-revoked rows. Defaults to false (active rules only). */
  includeRevoked: z.boolean().optional(),
  ...OptionalOffsetPageShape,
});
export const ListSalesHistoryTransfersResponseSchema = z.object({
  /** Newest first. */
  transfers: z.array(SalesHistoryTransferRowSchema),
  /** Every rule matching the filters, across all pages. */
  total: z.number().int(),
});
export type ListSalesHistoryTransfersInput = z.infer<
  typeof ListSalesHistoryTransfersInputSchema
>;
export type ListSalesHistoryTransfersResponse = z.infer<
  typeof ListSalesHistoryTransfersResponseSchema
>;

// ─── Create ──────────────────────────────────────────────────────────────────

/**
 * The fields that describe a rule, shared verbatim by `create` and `preview` —
 * a preview is the same rule, asked about rather than saved, so the two inputs
 * must not be able to drift.
 */
const salesHistoryTransferRuleShape = {
  mode: SalesHistoryTransferModeSchema,
  /** Share of the source's history the target receives. */
  copyPercent: z.number().gt(0).max(100).default(100),
  sourceProductV3Key: z.string().min(1).nullable().optional(),
  targetProductV3Key: z.string().min(1).nullable().optional(),
  sourceWarehouseId: z.string().uuid().nullable().optional(),
  targetWarehouseId: z.string().uuid().nullable().optional(),
  /** YYYY-MM-DD cutover; only months strictly before it are remapped. */
  effectiveBefore: z.iso.date().nullable().optional(),
  reason: z.string().max(1000).nullable().optional(),
};

/**
 * The four shape rules, as predicates + messages so `create` and `preview` say
 * exactly the same thing about the same malformed rule. They mirror the table's
 * CHECK constraints, so a bad rule is refused before it reaches Postgres.
 */
type RuleDimensions = {
  sourceProductV3Key?: string | null;
  targetProductV3Key?: string | null;
  sourceWarehouseId?: string | null;
  targetWarehouseId?: string | null;
};

const productPairComplete = (v: RuleDimensions): boolean =>
  (v.sourceProductV3Key == null) === (v.targetProductV3Key == null);
const PRODUCT_PAIR_MESSAGE = {
  message:
    "Source and target product keys must both be set or both be omitted.",
  path: ["targetProductV3Key"],
};

const warehousePairComplete = (v: RuleDimensions): boolean =>
  (v.sourceWarehouseId == null) === (v.targetWarehouseId == null);
const WAREHOUSE_PAIR_MESSAGE = {
  message: "Source and target warehouses must both be set or both be omitted.",
  path: ["targetWarehouseId"],
};

const remapsSomething = (v: RuleDimensions): boolean =>
  v.sourceProductV3Key != null || v.sourceWarehouseId != null;
const REMAPS_SOMETHING_MESSAGE = {
  message: "A rule must remap a product, a warehouse, or both.",
  path: ["sourceProductV3Key"],
};

// Non-identity: refused only when NEITHER dimension actually moves.
const isNotIdentity = (v: RuleDimensions): boolean =>
  (v.sourceProductV3Key ?? null) !== (v.targetProductV3Key ?? null) ||
  (v.sourceWarehouseId ?? null) !== (v.targetWarehouseId ?? null);
const NOT_IDENTITY_MESSAGE = {
  message: "A rule may not remap a coordinate onto itself.",
  path: ["targetProductV3Key"],
};

/**
 * Customer scope is deliberately absent in v1 — every rule authored here applies
 * to all customers.
 */
export const CreateSalesHistoryTransferInputSchema = z
  .object(salesHistoryTransferRuleShape)
  .refine(productPairComplete, PRODUCT_PAIR_MESSAGE)
  .refine(warehousePairComplete, WAREHOUSE_PAIR_MESSAGE)
  .refine(remapsSomething, REMAPS_SOMETHING_MESSAGE)
  .refine(isNotIdentity, NOT_IDENTITY_MESSAGE);
export const CreateSalesHistoryTransferResponseSchema = z.object({
  transfer: SalesHistoryTransferRowSchema,
});
export type CreateSalesHistoryTransferInput = z.infer<
  typeof CreateSalesHistoryTransferInputSchema
>;
export type CreateSalesHistoryTransferResponse = z.infer<
  typeof CreateSalesHistoryTransferResponseSchema
>;

// ─── Preview ─────────────────────────────────────────────────────────────────

/** Whole months of history a preview shows when the caller does not say. */
export const DEFAULT_PREVIEW_MONTHS_BACK = 12;

/**
 * A candidate rule, asked about instead of saved.
 *
 * Two fields beyond `create`:
 *  - `divisionId`, because the demand aggregate the preview reads is
 *    division-scoped (a forecast run reads one division's history at a time) and
 *    there is nothing on a rule that says which division to answer for;
 *  - `monthsBack`, the length of the window, ending at the last COMPLETE month.
 */
export const PreviewSalesHistoryTransferInputSchema = z
  .object({
    ...salesHistoryTransferRuleShape,
    divisionId: z.string().uuid(),
    /** Whole months of history to show. Defaults to 12. */
    monthsBack: z.number().int().min(1).max(120).optional(),
  })
  .refine(productPairComplete, PRODUCT_PAIR_MESSAGE)
  .refine(warehousePairComplete, WAREHOUSE_PAIR_MESSAGE)
  .refine(remapsSomething, REMAPS_SOMETHING_MESSAGE)
  .refine(isNotIdentity, NOT_IDENTITY_MESSAGE);

/**
 * One affected coordinate's monthly demand, with and without the candidate.
 *
 * A coordinate names whichever axes the rule actually moves: a product rule's
 * coordinates are SKUs (with `warehouseId` set only when the rule is scoped to
 * one branch), a warehouse-move rule's coordinates are warehouses and carry no
 * SKU. The series is therefore collapsed across the axis the coordinate leaves
 * open — see `previewSalesHistoryTransfer`.
 */
export const PreviewSalesHistoryTransferSeriesSchema = z.object({
  /** Display name for the coordinate ("SKU-A", "SKU-A @ Denver", "Denver"). */
  label: z.string(),
  productV3Key: z.string().nullable(),
  warehouseId: z.string().uuid().nullable(),
  months: z.array(
    z.object({
      /** YYYY-MM-01. */
      monthStart: z.string(),
      /** Demand as the buyer sees it today (current active rules + overrides). */
      before: z.number(),
      /** The same read with the candidate rule in force. */
      after: z.number(),
    }),
  ),
});
export type PreviewSalesHistoryTransferSeries = z.infer<
  typeof PreviewSalesHistoryTransferSeriesSchema
>;

export const PreviewSalesHistoryTransferResponseSchema = z.object({
  source: PreviewSalesHistoryTransferSeriesSchema.nullable(),
  target: PreviewSalesHistoryTransferSeriesSchema.nullable(),
});
export type PreviewSalesHistoryTransferInput = z.infer<
  typeof PreviewSalesHistoryTransferInputSchema
>;
export type PreviewSalesHistoryTransferResponse = z.infer<
  typeof PreviewSalesHistoryTransferResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeSalesHistoryTransferInputSchema = z.object({
  id: z.string().uuid(),
});
export const RevokeSalesHistoryTransferResponseSchema = z.object({
  transfer: SalesHistoryTransferRowSchema,
});
export type RevokeSalesHistoryTransferInput = z.infer<
  typeof RevokeSalesHistoryTransferInputSchema
>;
export type RevokeSalesHistoryTransferResponse = z.infer<
  typeof RevokeSalesHistoryTransferResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const salesHistoryTransfersContracts = {
  list: oc
    .input(ListSalesHistoryTransfersInputSchema)
    .output(ListSalesHistoryTransfersResponseSchema)
    .route({
      method: "GET",
      summary: "List sales history transfer rules",
      description:
        "Lists rules that copy or move sales history between SKUs or warehouses for forecasting, optionally filtered to a SKU or warehouse on either side and paged with limit/offset; active only by default.",
    }),
  create: oc
    .input(CreateSalesHistoryTransferInputSchema)
    .output(CreateSalesHistoryTransferResponseSchema)
    .route({
      method: "POST",
      summary: "Copy or transfer sales history",
      description:
        "Creates a rule that copies or moves a percentage of past demand from one SKU and/or warehouse to another for forecasting, optionally only before a cutover date.",
    }),
  preview: oc
    .input(PreviewSalesHistoryTransferInputSchema)
    .output(PreviewSalesHistoryTransferResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a sales history transfer",
      description:
        "Shows monthly demand before and after a candidate copy/transfer rule at both affected SKU-warehouse pairs over recent months, without saving it.",
    }),
  revoke: oc
    .input(RevokeSalesHistoryTransferInputSchema)
    .output(RevokeSalesHistoryTransferResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke a sales history transfer rule",
      description:
        "Deactivates a sales-history copy/transfer rule immediately; forecasting demand reverts to the unmapped history from the next read.",
    }),
};
